import { AfterViewInit, Directive, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { HashTriggerableService } from './hash-triggerable.service';
import { KEYBOARD } from '@finder/shared/constants/user-input.constants';

@Directive()
export abstract class HashTriggerable implements AfterViewInit, OnDestroy {
    elementId: string;

    protected destroy$ = new Subject<void>();

    private _isExpanded = false;
    get isExpanded() {
        return this._isExpanded;
    }

    constructor(
        protected hashTriggerableService: HashTriggerableService,
        protected route: ActivatedRoute,
    ) { }

    ngAfterViewInit() {
        // This will handle watching for hash changes and acting accordingly - including handling
        // a hash on load and auto-expanding the card for the hash ID
        this.route.fragment
            .pipe(takeUntil(this.destroy$))
            .subscribe(fragment => {
                this.handleHash(fragment);
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected click(event): void {
        // Allow keyboard control for a11y
        if (event.type === 'keydown') {
            switch (event.code) {
                case KEYBOARD.ENTER:
                case KEYBOARD.SPACE:
                    // ok...
                    event.preventDefault();
                    break;
                default:
                    return;
            }
        } else {
            // The drawer anchors have an href of '#' so we don't want anything happening when clicked
            event.preventDefault();
            event.stopPropagation();
        }

        // Act depending on the current state
        if (this.isExpanded === true) {
            this.collapse();
        } else {
            this.expand();
        }
    }

    protected expand(): void {
        this._isExpanded = true;
    }

    protected collapse(): void {
        this._isExpanded = false;
    }

    /**
     * This ONLY happens on load
     */
    protected handleHash(hash: string): void {
        // If there is a falsy hash value don't bother
        if (!hash) {
            return;
        }

        // If we have been here before and done that don't bother doing it again...
        if (this.hashTriggerableService.isTriggered) {
            return;
        }

        // We have a hash for triggering
        this.hashTriggerableService.enableTrigger();

        // Set the active element ID
        this.hashTriggerableService.setActiveElementId(hash);
    }
}
